import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import { Container, Button, Spinner, Alert, Table } from "react-bootstrap";
import {
  FiFileText,
  FiArrowLeft,
  FiChevronDown,
  FiDownload,
} from "react-icons/fi";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./MonthlyReport.css";

const METRIC_FIELDS = {
  telephonesalescalls: "Telephone",
  facetoface: "Face to Face",
  appointmentmade: "Appt Made",
  appointmentkept: "Appt Kept",
  factfind: "Fact Find",
  servicereviews: "Service Reviews",
  socialcalls: "Social Calls",
  highincomewealthy: "HIW",
  referrals: "Referrals",
  closinginterview: "Closing Int.",
  salesclose: "Sales Close",
};
const fieldKeys = Object.keys(METRIC_FIELDS);

const formatFullDate = (date) => {
  const options = { day: "2-digit", month: "short", year: "numeric" };
  return new Intl.DateTimeFormat("en-GB", options)
    .format(date)
    .replace(/ /g, "-");
};

// Custom dropdown for date selection
const CustomDropdown = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);
  return (
    <div className="custom-dropdown-wrapper" ref={dropdownRef}>
      <button
        type="button"
        className="custom-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{selectedOption ? selectedOption.label : placeholder}</span>
        <FiChevronDown className={`chevron-icon ${isOpen ? "open" : ""}`} />
      </button>
      {isOpen && (
        <ul className="custom-dropdown-panel">
          {options.map((option) => (
            <li
              key={option.value}
              className={`custom-dropdown-option ${
                value === option.value ? "selected" : ""
              }`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const UserMonthlyReport = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadRef = useRef(null);

  useEffect(() => {
    const fetchReport = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await api.get(
          `/api/users/me/monthly-entries?month=${
            selectedDate.getMonth() + 1
          }&year=${selectedDate.getFullYear()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          }
        );
        setReportData(response.data);
      } catch (err) {
        setError("Could not load report data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchReport();
  }, [selectedDate]);

  // Effect to handle clicks outside the download dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const daysInMonth = useMemo(
    () =>
      new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth() + 1,
        0
      ).getDate(),
    [selectedDate]
  );

  const monthlyTotals = useMemo(() => {
    const totals = { totalPoints: 0 };
    fieldKeys.forEach((key) => {
      totals[key] = 0;
    });
    reportData.forEach((entry) => {
      totals.totalPoints += Number(entry.points) || 0;
      fieldKeys.forEach((key) => {
        if (entry.hasOwnProperty(key)) totals[key] += Number(entry[key]) || 0;
      });
    });
    return totals;
  }, [reportData]);

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i,
        label: new Date(0, i).toLocaleString("en-US", { month: "long" }),
      })),
    []
  );

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => ({
      value: currentYear - i,
      label: String(currentYear - i),
    }));
  }, []);

  // =====================================================
  // Export Handlers
  // =====================================================
  const handleExport = (exportFunc) => {
    exportFunc();
    setDownloadMenuOpen(false);
  };

  const handleExportCSV = () => {
    const header = ["Date", ...Object.values(METRIC_FIELDS), "Daily Total"];
    const rows = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const currentDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        day
      );
      const dateStr = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const entry = reportData.find(
        (e) => new Date(e.entry_date).toISOString().split("T")[0] === dateStr
      );
      return [
        formatFullDate(currentDate),
        ...fieldKeys.map((k) => entry?.[k] || 0),
        entry?.points || 0,
      ];
    });
    const totalRow = [
      "Monthly Total",
      ...fieldKeys.map((key) => monthlyTotals[key]),
      monthlyTotals.totalPoints,
    ];

    const csvContent = [header, ...rows, totalRow]
      .map((r) => r.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `MonthlyReport_${
      monthOptions[selectedDate.getMonth()].label
    }_${selectedDate.getFullYear()}.csv`;
    link.click();
  };

  const handleExportExcel = () => {
    const header = [
      "Date",
      ...Object.values(METRIC_FIELDS),
      "Daily Total Points",
    ];
    const data = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const currentDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        day
      );
      const dateStr = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const entry = reportData.find(
        (e) => new Date(e.entry_date).toISOString().split("T")[0] === dateStr
      );
      const row = { Date: formatFullDate(currentDate) };
      fieldKeys.forEach((key) => (row[METRIC_FIELDS[key]] = entry?.[key] || 0));
      row["Daily Total Points"] = entry?.points || 0;
      return row;
    });

    const totalRow = { Date: "Monthly Total" };
    fieldKeys.forEach(
      (key) => (totalRow[METRIC_FIELDS[key]] = monthlyTotals[key])
    );
    totalRow["Daily Total Points"] = monthlyTotals.totalPoints;
    data.push(totalRow);

    const ws = XLSX.utils.json_to_sheet(data, { header });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Monthly Report");
    XLSX.writeFile(
      wb,
      `MonthlyReport_${
        monthOptions[selectedDate.getMonth()].label
      }_${selectedDate.getFullYear()}.xlsx`
    );
  };

  const handleExportPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    doc.text(
      `Monthly Report - ${
        monthOptions[selectedDate.getMonth()].label
      } ${selectedDate.getFullYear()}`,
      40,
      40
    );

    const tableData = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const currentDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        day
      );
      const dateStr = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const entry = reportData.find(
        (e) => new Date(e.entry_date).toISOString().split("T")[0] === dateStr
      );
      return [
        formatFullDate(currentDate),
        ...fieldKeys.map((key) => entry?.[key] || 0),
        entry?.points || 0,
      ];
    });

    doc.autoTable({
      head: [["Date", ...Object.values(METRIC_FIELDS), "Total Points"]],
      body: tableData,
      foot: [
        [
          "Monthly Total",
          ...fieldKeys.map((key) => monthlyTotals[key]),
          monthlyTotals.totalPoints,
        ],
      ],
      startY: 60,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [33, 37, 41] },
      footStyles: { fillColor: [73, 80, 87], fontStyle: "bold" },
    });
    doc.save(
      `MonthlyReport_${
        monthOptions[selectedDate.getMonth()].label
      }_${selectedDate.getFullYear()}.pdf`
    );
  };

  const renderContent = () => {
    if (isLoading)
      return (
        <div className="text-center p-5">
          <Spinner animation="border" />
        </div>
      );
    if (error)
      return (
        <Alert variant="danger" className="m-3">
          {error}
        </Alert>
      );

    return (
      <div className="report-table-wrapper">
        <Table hover responsive className="report-table">
          <thead>
            <tr>
              <th>Date</th>
              {fieldKeys.map((key) => (
                <th key={key}>{METRIC_FIELDS[key]}</th>
              ))}
              <th>Daily Total Points</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const currentDate = new Date(
                selectedDate.getFullYear(),
                selectedDate.getMonth(),
                day
              );
              const dateStr = `${currentDate.getFullYear()}-${String(
                currentDate.getMonth() + 1
              ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const entry = reportData.find(
                (e) =>
                  new Date(e.entry_date).toISOString().split("T")[0] === dateStr
              );
              return (
                <tr key={day}>
                  <td>{formatFullDate(currentDate)}</td>
                  {fieldKeys.map((key) => (
                    <td key={key}>{entry?.[key] || 0}</td>
                  ))}
                  <td>{entry?.points || 0}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>Monthly Total</td>
              {fieldKeys.map((key) => (
                <td key={key}>{monthlyTotals[key]}</td>
              ))}
              <td>{monthlyTotals.totalPoints}</td>
            </tr>
          </tfoot>
        </Table>
      </div>
    );
  };

  return (
    <div className="monthly-report-page">
      <Container>
        <div className="page-header">
          <h2 className="page-title">
            <FiFileText /> My Monthly Report
          </h2>
          <Button
            variant="link"
            className="back-button"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft /> Back
          </Button>
        </div>

        <div className="filters-row">
          <div className="date-selectors">
            <CustomDropdown
              options={monthOptions}
              value={selectedDate.getMonth()}
              onChange={(month) =>
                setSelectedDate(new Date(selectedDate.getFullYear(), month))
              }
              placeholder="Select Month"
            />
            <CustomDropdown
              options={yearOptions}
              value={selectedDate.getFullYear()}
              onChange={(year) =>
                setSelectedDate(new Date(year, selectedDate.getMonth()))
              }
              placeholder="Select Year"
            />
          </div>
          <div className="download-dropdown-wrapper" ref={downloadRef}>
            <Button
              variant="primary"
              size="sm"
              className="download-trigger"
              onClick={() => setDownloadMenuOpen(!downloadMenuOpen)}
            >
              <FiDownload /> Download
            </Button>
            {downloadMenuOpen && (
              <ul className="download-dropdown-panel">
                <li onClick={() => handleExport(handleExportExcel)}>
                  Excel (.xlsx)
                </li>
                <li onClick={() => handleExport(handleExportPDF)}>
                  PDF (.pdf)
                </li>
                <li onClick={() => handleExport(handleExportCSV)}>
                  CSV (.csv)
                </li>
              </ul>
            )}
          </div>
        </div>

        <div className="report-card">{renderContent()}</div>
      </Container>
    </div>
  );
};

export default UserMonthlyReport;
